import { useState, type ReactNode } from 'react';

/**
 * Drag to reorder, without a drag-and-drop library.
 *
 * The HTML5 drag events do everything needed here and cost nothing to ship. A library would
 * add a dependency and a bundle for a list of at most a few dozen rows.
 *
 * **The arrows stay.** Native drag-and-drop is mouse-only — it does not work from a keyboard
 * and is awkward on a touchpad — so removing them would take the feature away from anyone who
 * cannot drag, which is not a trade an admin panel should make. Drag is the faster path for
 * people who can use it, not the only one.
 */
export function Reorderable<T>({
  items,
  keyOf,
  onReorder,
  disabled = false,
  children,
}: {
  items: T[];
  keyOf: (item: T) => string;
  /** Called with the whole list in its new order, which is what the API takes. */
  onReorder: (ordered: T[]) => void;
  disabled?: boolean;
  children: (item: T, index: number, handle: ReactNode) => ReactNode;
}): JSX.Element {
  const [draggingKey, setDraggingKey] = useState<string | null>(null);
  const [overKey, setOverKey] = useState<string | null>(null);

  const move = (fromKey: string, toKey: string): void => {
    if (fromKey === toKey) return;

    const from = items.findIndex((item) => keyOf(item) === fromKey);
    const to = items.findIndex((item) => keyOf(item) === toKey);
    if (from === -1 || to === -1) return;

    const next = [...items];
    const [moved] = next.splice(from, 1);
    if (moved === undefined) return;

    next.splice(to, 0, moved);
    onReorder(next);
  };

  return (
    <>
      {items.map((item, index) => {
        const key = keyOf(item);

        const handle = (
          <span
            aria-hidden
            title="Drag to reorder"
            style={{
              cursor: disabled ? 'default' : 'grab',
              padding: '0 6px',
              color: 'var(--muted, #5c6672)',
              fontSize: 18,
              lineHeight: 1,
              userSelect: 'none',
            }}
          >
            ⠿
          </span>
        );

        return (
          <div
            key={key}
            draggable={!disabled}
            onDragStart={(event) => {
              setDraggingKey(key);
              // Firefox refuses to start a drag unless something is on the transfer.
              event.dataTransfer.setData('text/plain', key);
              event.dataTransfer.effectAllowed = 'move';
            }}
            onDragEnd={() => {
              setDraggingKey(null);
              setOverKey(null);
            }}
            onDragOver={(event) => {
              if (draggingKey === null) return;
              event.preventDefault();
              setOverKey(key);
            }}
            onDrop={(event) => {
              event.preventDefault();
              const fromKey = draggingKey ?? event.dataTransfer.getData('text/plain');
              if (fromKey !== '') move(fromKey, key);
              setDraggingKey(null);
              setOverKey(null);
            }}
            style={{
              opacity: draggingKey === key ? 0.4 : 1,
              // A line where it will land, rather than moving the rows around under the
              // cursor: a list that reflows mid-drag is very hard to aim at.
              borderTop:
                overKey === key && draggingKey !== key
                  ? '3px solid var(--accent, #1f5f8b)'
                  : '3px solid transparent',
            }}
          >
            {children(item, index, handle)}
          </div>
        );
      })}
    </>
  );
}
